/* eslint-disable no-param-reassign */
import { captureMessage } from '@sentry/react';
import { ValuationApproachDCF } from 'api';
import { ValuationsApproach } from 'common/types/valuation';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  APPROACH_SELECTION,
  EBITDA_APPROACH_SELECTION,
  EBITDA_GPC_MULTIPLE,
  EBITDA_GPC_MULTIPLE_SELECTION,
  GPC_MULTIPLE,
  GPC_MULTIPLE_SELECTION,
  REM_TVT_SECOND_COLUMN,
  REVENUE_APPROACH_SELECTION,
  REVENUE_GPC_MULTIPLE,
  REVENUE_GPC_MULTIPLE_SELECTION,
  SELECT_MULTIPLE_ALIASES,
} from 'pages/Valuations/util/constants';
import getSelectionFromTVT from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/dcfTerminalValue/util/getSelectionFromTVT';
import { DCF_FINANCIAL_PERIOD_TABLE_NAME } from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/utilities/constants';
import { getSelectionCellOptions } from 'pages/ValuationsAllocation/util';
import { parseDatabaseValue } from 'utilities';
import { ReverseParserProps } from './types';
import getCurrentSelectedApproach from '../../util/getCurrentSelectedApproach';
import { getSelectedApproachAliases, TVTApproachType } from '../../util/getSelectedApproachAliases';
import updateApproachesSelectionsInTableData from '../../util/updateApproachesSelectionsInTable';

const reverseParser = ({ cells, tableData, approachOptions }: ReverseParserProps) => {
  /**
   * @description
   * This function updates the selected GPC multiple for a given cell in the table data.
   * It determines the current selected approach based on the provided approaches and the
   * table's current approach. It then retrieves the specific GPC or GPT approach and the
   * available multiple options for selection. The function calculates the selection based
   * on the cell and value provided, and writes the selection to the appropriate property
   * in the table data.
   *
   * @param {ValuationAllocation[] | undefined} approaches - The list of valuation approaches.
   * @param {boolean} isSecondColumn - Indicates if the selection is for the second column.
   * @param {ICell<object>} cell - The cell in the table where the selection is made.
   * @param {string} value - The value to be selected.
   * @returns {void}
   */
  const handleGpcMultipleSelection = (isSecondColumn: boolean, cell: ICell<object>, value: string): void => {
    if (!tableData.approach.valuations_approach_dcf) {
      return;
    }

    const selectedApproach = getCurrentSelectedApproach({
      approaches: tableData.approaches,
      approach: tableData.approach,
      isSecondColumn,
    }) as ValuationsApproach;

    const specificApproach = selectedApproach?.valuations_approach_gpc ?? selectedApproach?.valuations_approach_gpt;
    const multipleOptions = getSelectionCellOptions({
      isDCF: true,
      specificApproach,
    });
    const selection = getSelectionFromTVT({ cell, value, multipleOptions, specificApproach });
    const propToWriteTo
      = isSecondColumn || cell.alias.includes('ebitda') ? EBITDA_GPC_MULTIPLE_SELECTION : REVENUE_GPC_MULTIPLE_SELECTION;

    const key = propToWriteTo as keyof ValuationApproachDCF;
    (tableData.approach.valuations_approach_dcf[key] as typeof selection) = selection;
  };

  /**
   * @description This function updates the GPC multiple for a given cell in the table data.
   * @param isSecondColumn
   * @param dbValue {@type any} any because the function that parsed db value will look at the cell and return correct type of data
   * @returns {void}
   *
   */
  const handleGpcMultiple = (isSecondColumn: boolean, dbValue: any): void => {
    if (!tableData.approach.valuations_approach_dcf) {
      return;
    }

    const propToWriteTo = isSecondColumn ? EBITDA_GPC_MULTIPLE : REVENUE_GPC_MULTIPLE;
    const key = propToWriteTo as keyof ValuationApproachDCF;
    (tableData.approach.valuations_approach_dcf[key] as typeof dbValue) = dbValue;
  };

  /**
   * Handles the selection of an alias in a valuation table.
   *
   * This function updates the selected alias for a given cell in the table data.
   * It determines the appropriate property to write to based on the cell's alias,
   * finds the selected approach from the available approach options, and retrieves
   * the corresponding approach type. It then updates the alias and the selected
   * approach value in the table data.
   *
   * @param {ICell<object>} cell - The cell in the table where the selection is made.
   * @param {any} value - The value to be selected.
   */
  const handleSelectionAlias = (alias: TVTApproachType, value: any, isSecondColumn?: boolean): void => {
    const selectedApproach = approachOptions?.find(({ value: approachValue }) => approachValue === value);
    const approachType = selectedApproach?.type ?? '';
    const updatedAlias = getSelectedApproachAliases(alias, approachType, isSecondColumn);
    const selectedApproachValue
      = selectedApproach?.value === 0 || selectedApproach?.value === undefined ? null : selectedApproach?.value;
    if (selectedApproachValue) {
      updateApproachesSelectionsInTableData({
        tableData,
        valueFromSelector: selectedApproachValue,
        updatedAlias,
      });
    }
  };

  const handleStandardCell = (alias: keyof ValuationApproachDCF, dbValue: any): void => {
    if (!tableData.approach.valuations_approach_dcf) {
      return;
    }

    (tableData.approach.valuations_approach_dcf[alias] as typeof dbValue) = dbValue;
  };

  // iterate over each cell, and update the table data accordingly

  Object.values(cells).forEach(cell => {
    const { value, dbType, format, gridType, dbDecimalPlaces, origin, alias, isTitleCell, columnLegend } = cell;

    const isUpdatableCell = alias && !isTitleCell;
    if (!isUpdatableCell || !tableData?.approach?.valuations_approach_dcf) {
      return;
    }

    const dbValue = parseDatabaseValue({
      type: dbType,
      value,
      format,
      allowEmptyValues: true,
      dontMultiply: true,
      gridType,
      decimalPlaces: dbDecimalPlaces as number,
    });

    const isModifiedPropToWriteTo = [
      GPC_MULTIPLE,
      REVENUE_APPROACH_SELECTION,
      EBITDA_APPROACH_SELECTION,
      APPROACH_SELECTION,
      ...SELECT_MULTIPLE_ALIASES,
    ].includes(alias);

    const isDCFFinancialPeriod = origin === DCF_FINANCIAL_PERIOD_TABLE_NAME;

    const isStandardCell = !isDCFFinancialPeriod && !isModifiedPropToWriteTo;
    if (isStandardCell) {
      return handleStandardCell(alias as keyof ValuationApproachDCF, dbValue);
    }

    const isSecondColumn = columnLegend === REM_TVT_SECOND_COLUMN;
    switch (alias) {
      case EBITDA_GPC_MULTIPLE_SELECTION:
      case REVENUE_GPC_MULTIPLE_SELECTION:
      case GPC_MULTIPLE_SELECTION:
        return handleGpcMultipleSelection(isSecondColumn, cell, value as string);
      case GPC_MULTIPLE:
        return handleGpcMultiple(isSecondColumn, dbValue);
      case REVENUE_APPROACH_SELECTION:
      case EBITDA_APPROACH_SELECTION:
      case APPROACH_SELECTION:
        return handleSelectionAlias(alias, value, isSecondColumn);
      default:
        captureMessage(`Unhandled cell alias during DCF approach reverse parsing: ${alias}`, {
          level: 'warning',
        });
        console.warn(`Unhandled cell alias: ${alias}`);
        break;
    }
  });
};
export default reverseParser;
